import React, { useState, useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckIcon, XIcon, ShieldCheckIcon, ZapIcon, FileTextIcon, ChevronDownIcon, ChevronUpIcon, LockIcon, DownloadIcon, AlertTriangleIcon } from 'lucide-react';
// ─── Animation Variants ───────────────────────────────────────────────────────
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12
    }
  }
};
const fadeIn = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.7
    }
  }
};
// ─── Scroll-triggered Section Wrapper ────────────────────────────────────────
function AnimatedSection({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <motion.div ref={ref} variants={fadeUp} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className={className} data-id="element-2">
      {children}
    </motion.div>;
}
// ─── Red Divider ──────────────────────────────────────────────────────────────
function RedDivider() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  return <div ref={ref} className="flex items-center gap-3 my-2" data-id="element-3">
      <motion.div className="h-0.5 bg-red-600" initial={{
      width: 0
    }} animate={isInView ? {
      width: 48
    } : {
      width: 0
    }} transition={{
      duration: 0.6,
      ease: 'easeOut'
    }} data-id="element-4" />
      <div className="w-1.5 h-1.5 rounded-full bg-red-600" data-id="element-5" />
    </div>;
}
// ─── Section Label ────────────────────────────────────────────────────────────
function SectionLabel({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="flex items-center gap-3 mb-4" data-id="element-6">
      <RedDivider data-id="element-7" />
      <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" style={{
      fontFamily: 'Inter, sans-serif'
    }} data-id="element-8">
        {children}
      </span>
    </div>;
}
// ─── FAQ Item ─────────────────────────────────────────────────────────────────
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="border border-neutral-800 rounded-lg overflow-hidden" data-id="element-9">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left bg-neutral-900 hover:bg-neutral-800 transition-colors duration-200" aria-expanded={open} data-id="element-10">
        <span className="text-neutral-100 font-medium text-sm md:text-base pr-4" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-11">
          {question}
        </span>
        {open ? <ChevronUpIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-12" /> : <ChevronDownIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-13" />}
      </button>
      <motion.div initial={false} animate={{
      height: open ? 'auto' : 0,
      opacity: open ? 1 : 0
    }} transition={{
      duration: 0.3,
      ease: 'easeInOut'
    }} className="overflow-hidden" data-id="element-14">
        <p className="p-5 pt-0 text-neutral-400 text-sm leading-relaxed bg-neutral-900" style={{
        fontFamily: 'Inter, sans-serif'
      }} data-id="element-15">
          {answer}
        </p>
      </motion.div>
    </div>;
}
type ModalType = 'privacy' | 'terms' | null;
function LegalModal({
  type,
  onClose
}: {
  type: ModalType;
  onClose: () => void;
}) {
  if (!type) return null;
  const isPrivacy = type === 'privacy';
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{
    backgroundColor: 'rgba(0,0,0,0.85)'
  }} onClick={onClose} role="dialog" aria-modal="true" aria-label={isPrivacy ? 'Política de Privacidad' : 'Términos de Servicio'} data-id="element-16">
      <div className="relative w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-2xl border border-neutral-800 p-8" style={{
      backgroundColor: '#111111'
    }} onClick={e => e.stopPropagation()} data-id="element-17">
        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-neutral-400 hover:text-white hover:bg-neutral-800 transition-colors" aria-label="Cerrar" data-id="element-18">
          <XIcon className="w-4 h-4" data-id="element-19" />
        </button>

        {isPrivacy ? <>
            <h2 className="text-white mb-6" style={{
          fontFamily: 'Bebas Neue, sans-serif',
          fontSize: '2rem',
          letterSpacing: '0.05em'
        }} data-id="element-20">
              Política de Privacidad
            </h2>
            <div className="space-y-5 text-neutral-400 text-sm leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-21">
              <p data-id="element-22">Última actualización: 2024</p>
              <div data-id="element-23">
                <h3 className="text-neutral-200 font-semibold mb-2" data-id="element-24">
                  1. Información que Recopilamos
                </h3>
                <p data-id="element-25">
                  Al realizar una compra, recopilamos únicamente la información
                  necesaria para procesar tu pago y entregarte el producto
                  digital. Esto incluye tu dirección de correo electrónico para
                  el envío del acceso al manual.
                </p>
              </div>
              <div data-id="element-26">
                <h3 className="text-neutral-200 font-semibold mb-2" data-id="element-27">
                  2. Uso de la Información
                </h3>
                <p data-id="element-28">
                  La información recopilada se utiliza exclusivamente para:
                  procesar tu transacción, entregarte el producto digital
                  adquirido, y enviarte comunicaciones relacionadas con tu
                  compra.
                </p>
              </div>
              <div data-id="element-29">
                <h3 className="text-neutral-200 font-semibold mb-2" data-id="element-30">
                  3. Protección de Datos
                </h3>
                <p data-id="element-31">
                  No vendemos, intercambiamos ni transferimos tu información
                  personal a terceros. Todos los pagos son procesados a través
                  de plataformas seguras con encriptación SSL. No almacenamos
                  datos de tarjetas de crédito.
                </p>
              </div>
              <div data-id="element-32">
                <h3 className="text-neutral-200 font-semibold mb-2" data-id="element-33">
                  4. Cookies
                </h3>
                <p data-id="element-34">
                  Este sitio puede utilizar cookies básicas para mejorar la
                  experiencia de navegación. No utilizamos cookies de
                  seguimiento de terceros para publicidad.
                </p>
              </div>
              <div data-id="element-35">
                <h3 className="text-neutral-200 font-semibold mb-2" data-id="element-36">
                  5. Producto Digital
                </h3>
                <p data-id="element-37">
                  "Cero Emoción: El Manual de 11 PM" es un producto digital
                  (PDF). Una vez entregado el acceso, no se realizan
                  devoluciones salvo en casos excepcionales evaluados
                  individualmente.
                </p>
              </div>
              <div data-id="element-38">
                <h3 className="text-neutral-200 font-semibold mb-2" data-id="element-39">
                  6. Cambios a Esta Política
                </h3>
                <p data-id="element-40">
                  Nos reservamos el derecho de actualizar esta política en
                  cualquier momento. Los cambios serán efectivos inmediatamente
                  después de su publicación en esta página.
                </p>
              </div>
            </div>
          </> : <>
            <h2 className="text-white mb-6" style={{
          fontFamily: 'Bebas Neue, sans-serif',
          fontSize: '2rem',
          letterSpacing: '0.05em'
        }} data-id="element-41">
              Términos de Servicio
            </h2>
            <div className="space-y-5 text-neutral-400 text-sm leading-relaxed" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-42">
              <p data-id="element-43">Última actualización: 2024</p>
              <div data-id="element-44">
                <h3 className="text-neutral-200 font-semibold mb-2" data-id="element-45">
                  1. Aceptación de Términos
                </h3>
                <p data-id="element-46">
                  Al adquirir "Cero Emoción: El Manual de 11 PM", aceptas estos
                  términos de servicio en su totalidad. Si no estás de acuerdo
                  con alguno de estos términos, no debes realizar la compra.
                </p>
              </div>
              <div data-id="element-47">
                <h3 className="text-neutral-200 font-semibold mb-2" data-id="element-48">
                  2. Naturaleza del Producto
                </h3>
                <p data-id="element-49">
                  Este producto es un manual educativo de psicología del trading
                  en formato PDF. No constituye asesoramiento financiero, de
                  inversión, ni garantía de resultados económicos. El trading de
                  opciones conlleva riesgo significativo de pérdida de capital.
                </p>
              </div>
              <div data-id="element-50">
                <h3 className="text-neutral-200 font-semibold mb-2" data-id="element-51">
                  3. Licencia de Uso
                </h3>
                <p data-id="element-52">
                  Al comprar este producto, obtienes una licencia personal e
                  intransferible para uso individual. Está prohibida la
                  reproducción, distribución, reventa o compartición del
                  contenido sin autorización expresa por escrito.
                </p>
              </div>
              <div data-id="element-53">
                <h3 className="text-neutral-200 font-semibold mb-2" data-id="element-54">
                  4. Descargo de Responsabilidad
                </h3>
                <p data-id="element-55">
                  Los resultados descritos en esta página son ilustrativos. Los
                  resultados individuales varían significativamente. No
                  garantizamos ganancias en el mercado de valores. El autor no
                  es responsable de pérdidas financieras derivadas de la
                  aplicación del contenido.
                </p>
              </div>
              <div data-id="element-56">
                <h3 className="text-neutral-200 font-semibold mb-2" data-id="element-57">
                  5. Política de Reembolso
                </h3>
                <p data-id="element-58">
                  Dado que se trata de un producto digital con acceso inmediato,
                  no se realizan reembolsos automáticos. Casos excepcionales
                  serán evaluados individualmente dentro de los 14 días
                  posteriores a la compra.
                </p>
              </div>
              <div data-id="element-59">
                <h3 className="text-neutral-200 font-semibold mb-2" data-id="element-60">
                  6. Ley Aplicable
                </h3>
                <p data-id="element-61">
                  Estos términos se rigen por las leyes aplicables en la
                  jurisdicción del comprador. Cualquier disputa será resuelta de
                  buena fe entre las partes.
                </p>
              </div>
            </div>
          </>}
      </div>
    </div>;
}
// ─── Main Landing Page ────────────────────────────────────────────────────────
export function LandingPage() {
  const [legalModal, setLegalModal] = useState<ModalType>(null);
  return <main className="min-h-screen w-full" style={{
    backgroundColor: '#0a0a0a',
    fontFamily: 'Inter, sans-serif'
  }} data-id="element-62">
      <LegalModal type={legalModal} onClose={() => setLegalModal(null)} data-id="element-63" />

      {/* ── TICKER BAR ── */}
      <div className="bg-red-700 overflow-hidden py-2" data-id="element-64">
        <div className="ticker-inner flex whitespace-nowrap" data-id="element-65">
          {[...Array(2)].map((_, i) => <span key={i} className="text-white text-xs font-semibold tracking-widest uppercase px-8 flex-shrink-0" data-id="element-66">
              ⚡ ACCESO INMEDIATO &nbsp;•&nbsp; TRADING DE OPCIONES
              &nbsp;•&nbsp; PROTEGE TU CAPITAL &nbsp;•&nbsp; CERO EMOCIÓN
              &nbsp;•&nbsp; 11 PM PROTOCOL &nbsp;•&nbsp; SPY · QQQ · NVIDIA ·
              TESLA &nbsp;•&nbsp; STOP LOSS MENTAL &nbsp;•&nbsp; ACCESO
              INMEDIATO &nbsp;•&nbsp; TRADING DE OPCIONES &nbsp;•&nbsp; PROTEGE
              TU CAPITAL &nbsp;•&nbsp; CERO EMOCIÓN &nbsp;•&nbsp; 11 PM PROTOCOL
              &nbsp;•&nbsp; SPY · QQQ · NVIDIA · TESLA &nbsp;•&nbsp; STOP LOSS
              MENTAL &nbsp;•&nbsp;
            </span>)}
        </div>
      </div>

      {/* ── HERO SECTION ── */}
      <section className="relative overflow-hidden" style={{
      backgroundColor: '#0a0a0a'
    }} data-id="element-67">
        {/* Background grid */}
        <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'linear-gradient(rgba(220,38,38,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(220,38,38,0.3) 1px, transparent 1px)',
        backgroundSize: '60px 60px'
      }} data-id="element-68" />
        {/* Red glow bottom */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-64 bg-red-700 opacity-10 blur-3xl rounded-full" data-id="element-69" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20" data-id="element-70">
          <motion.div variants={stagger} initial="hidden" animate="visible" className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16" data-id="element-71">
            {/* Left: Text */}
            <div className="flex-1 text-center lg:text-left" data-id="element-72">
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-red-700 bg-opacity-20 border border-red-700 rounded-full px-4 py-1.5 mb-6" data-id="element-73">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" data-id="element-74" />
                <span className="text-red-400 text-xs font-semibold tracking-widest uppercase" data-id="element-75">
                  Acceso Inmediato
                </span>
              </motion.div>

              <motion.h1 variants={fadeUp} className="text-white leading-none mb-4" style={{
              fontFamily: 'Bebas Neue, sans-serif',
              fontSize: 'clamp(3rem, 8vw, 6rem)',
              letterSpacing: '0.02em'
            }} data-id="element-76">
                Cero Emoción:
                <br data-id="element-77" />
                <span className="text-red-500" data-id="element-78">El Manual</span> de 11 PM
              </motion.h1>

              <motion.p variants={fadeUp} className="text-neutral-300 text-lg md:text-xl leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0" data-id="element-79">
                Detén la racha de pérdidas en tus opciones y recupera el control
                de tu cuenta antes de la apertura de mañana en Wall Street.
              </motion.p>

              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start" data-id="element-80">
                <a href="#precio" className="cta-pulse inline-flex items-center gap-3 bg-red-600 hover:bg-red-500 text-white font-bold text-lg px-8 py-4 rounded-lg transition-colors duration-200 w-full sm:w-auto justify-center" style={{
                fontFamily: 'Inter, sans-serif'
              }} data-id="element-81">
                  <DownloadIcon className="w-5 h-5" data-id="element-82" />
                  DESCARGAR EL MANUAL AHORA
                </a>
              </motion.div>

              <motion.div variants={fadeUp} className="mt-5 flex items-center gap-4 justify-center lg:justify-start" data-id="element-83">
                <span className="text-amber-400 font-bold text-3xl" style={{
                fontFamily: 'Bebas Neue, sans-serif',
                letterSpacing: '0.05em'
              }} data-id="element-84">
                  $6.37 USD
                </span>
                <span className="text-neutral-500 text-sm" data-id="element-85">
                  · Pago único · Acceso inmediato
                </span>
              </motion.div>
            </div>

            {/* Right: Product Image */}
            <motion.div variants={fadeIn} className="flex-1 flex justify-center lg:justify-end" data-id="element-86">
              <div className="relative" data-id="element-87">
                <div className="absolute inset-0 bg-red-600 opacity-20 blur-3xl rounded-3xl scale-110" data-id="element-88" />
                <img src="https://cdn.magicpatterns.com/uploads/thRWGNfYXsvr833LtEiwu2/cero_emociones.png" alt="Cero Emoción: El Manual de 11 PM — mostrado en laptop, tablet y teléfono" className="relative w-full max-w-lg rounded-2xl shadow-2xl" data-id="element-89" />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── OPENING HOOK ── */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{
      backgroundColor: '#111111'
    }} data-id="element-90">
        <div className="max-w-3xl mx-auto" data-id="element-91">
          <AnimatedSection data-id="element-92">
            <div className="border border-neutral-800 rounded-2xl p-8 md:p-12" style={{
            backgroundColor: '#161616'
          }} data-id="element-93">
              <p className="text-red-500 text-sm font-bold tracking-widest uppercase mb-6" data-id="element-94">
                SON LAS 11:00 PM.
              </p>
              <p className="text-neutral-200 text-lg md:text-xl leading-relaxed mb-5" data-id="element-95">
                Estás mirando el techo, con un nudo en el estómago y la pantalla
                del broker grabada en la retina. Hoy no fue un mal día de
                mercado; fue un día de{' '}
                <span className="text-white font-semibold" data-id="element-96">autosabotaje</span>.
              </p>
              <p className="text-neutral-300 text-base md:text-lg leading-relaxed mb-5" data-id="element-97">
                Perdiste{' '}
                <span className="text-red-400 font-semibold" data-id="element-98">
                  $500, $1,000 o quizás más
                </span>{' '}
                — no porque no supieras leer las gráficas, sino porque el miedo
                te congeló cuando debiste salir, o la avaricia te hizo aguantar
                una posición de SPY que terminó en cero.
              </p>
              <p className="text-neutral-400 text-base leading-relaxed" data-id="element-99">
                Lo peor es que juraste que no volvería a pasar...{' '}
                <span className="text-neutral-200 italic" data-id="element-100">y pasó.</span>
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── THE PROBLEM ── */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{
      backgroundColor: '#0a0a0a'
    }} data-id="element-101">
        <div className="max-w-4xl mx-auto" data-id="element-102">
          <AnimatedSection data-id="element-103">
            <SectionLabel data-id="element-104">El Problema Real</SectionLabel>
            <h2 className="text-white mb-6" style={{
            fontFamily: 'Bebas Neue, sans-serif',
            fontSize: 'clamp(2.2rem, 5vw, 3.5rem)',
            letterSpacing: '0.03em'
          }} data-id="element-105">
              El Trading de Opciones es un Deporte de Combate Mental
            </h2>
            <p className="text-neutral-300 text-base md:text-lg leading-relaxed mb-8" data-id="element-106">
              La volatilidad de los contratos 0DTE y la velocidad de las
              acciones tecnológicas como NVIDIA o Tesla disparan químicos en tu
              cerebro que anulan tu lógica. Cuando estás dentro de una operación
              perdedora, dejas de ser un trader y te conviertes en una presa
              asustada.
            </p>
          </AnimatedSection>

          <AnimatedSection data-id="element-107">
            <div className="rounded-xl p-6 md:p-8 border border-red-900 mt-6" style={{
            backgroundColor: '#1a0a0a'
          }} data-id="element-108">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center" data-id="element-109">
                <div data-id="element-110">
                  <p className="text-red-500 font-bold text-2xl mb-1" style={{
                  fontFamily: 'Bebas Neue, sans-serif',
                  letterSpacing: '0.05em'
                }} data-id="element-111">
                    TU PULSO SUBE.
                  </p>
                  <p className="text-neutral-500 text-sm" data-id="element-112">
                    Adrenalina bloquea el pensamiento racional
                  </p>
                </div>
                <div className="border-t md:border-t-0 md:border-l md:border-r border-red-900 pt-4 md:pt-0 md:px-6" data-id="element-113">
                  <p className="text-red-500 font-bold text-2xl mb-1" style={{
                  fontFamily: 'Bebas Neue, sans-serif',
                  letterSpacing: '0.05em'
                }} data-id="element-114">
                    TU VISIÓN SE NUBLA.
                  </p>
                  <p className="text-neutral-500 text-sm" data-id="element-115">
                    Cortisol estrecha el foco cognitivo
                  </p>
                </div>
                <div className="border-t md:border-t-0 pt-4 md:pt-0" data-id="element-116">
                  <p className="text-red-500 font-bold text-2xl mb-1" style={{
                  fontFamily: 'Bebas Neue, sans-serif',
                  letterSpacing: '0.05em'
                }} data-id="element-117">
                    TU CUENTA SE VACÍA.
                  </p>
                  <p className="text-neutral-500 text-sm" data-id="element-118">
                    Trading de revancha destruye el capital
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection className="mt-8" data-id="element-119">
            <p className="text-neutral-300 text-base md:text-lg leading-relaxed" data-id="element-120">
              Tu pulso sube, tu visión se nubla y terminas haciendo{' '}
              <span className="text-red-400 font-semibold" data-id="element-121">
                "trading de revancha"
              </span>{' '}
              — intentando recuperar en 10 minutos lo que el mercado te quitó en
              horas. El resultado es siempre el mismo: una cuenta cada vez más
              pequeña y una frustración que no te deja vivir.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* ── WHY PREVIOUS SOLUTIONS FAILED ── */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{
      backgroundColor: '#111111'
    }} data-id="element-122">
        <div className="max-w-4xl mx-auto" data-id="element-123">
          <AnimatedSection data-id="element-124">
            <SectionLabel data-id="element-125">Por Qué Fallaste Antes</SectionLabel>
            <h2 className="text-white mb-10" style={{
            fontFamily: 'Bebas Neue, sans-serif',
            fontSize: 'clamp(2rem, 5vw, 3.2rem)',
            letterSpacing: '0.03em'
          }} data-id="element-126">
              Por Qué Lo Que Intentaste No Funcionó
            </h2>
          </AnimatedSection>

          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} className="space-y-4" data-id="element-127">
            <motion.div variants={fadeUp} className="flex items-start gap-4 p-5 rounded-xl border border-neutral-800" style={{
            backgroundColor: '#161616'
          }} data-id="element-128">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-900 bg-opacity-50 flex items-center justify-center mt-0.5" data-id="element-129">
                <XIcon className="w-4 h-4 text-red-500" data-id="element-130" />
              </div>
              <div data-id="element-131">
                <p className="text-neutral-100 font-semibold mb-1" data-id="element-132">
                  Intentaste usar "fuerza de voluntad" contra la dopamina
                </p>
                <p className="text-neutral-500 text-sm leading-relaxed" data-id="element-133">
                  La biología siempre gana. No es un problema de carácter, es un
                  problema de arquitectura cerebral.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="flex items-start gap-4 p-5 rounded-xl border border-neutral-800" style={{
            backgroundColor: '#161616'
          }} data-id="element-134">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-900 bg-opacity-50 flex items-center justify-center mt-0.5" data-id="element-135">
                <XIcon className="w-4 h-4 text-red-500" data-id="element-136" />
              </div>
              <div data-id="element-137">
                <p className="text-neutral-100 font-semibold mb-1" data-id="element-138">
                  Leíste libros de psicología general
                </p>
                <p className="text-neutral-500 text-sm leading-relaxed" data-id="element-139">
                  Que no entienden la velocidad de una opción perdiendo valor.
                  El mercado no espera tu reflexión.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="flex items-start gap-4 p-5 rounded-xl border border-neutral-800" style={{
            backgroundColor: '#161616'
          }} data-id="element-140">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-900 bg-opacity-50 flex items-center justify-center mt-0.5" data-id="element-141">
                <XIcon className="w-4 h-4 text-red-500" data-id="element-142" />
              </div>
              <div data-id="element-143">
                <p className="text-neutral-100 font-semibold mb-1" data-id="element-144">
                  Te enfocaste en aprender más indicadores técnicos
                </p>
                <p className="text-neutral-500 text-sm leading-relaxed" data-id="element-145">
                  El problema no es la gráfica. Es tu dedo en el botón cuando el
                  miedo toma el control.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="flex items-start gap-4 p-5 rounded-xl border border-neutral-800" style={{
            backgroundColor: '#161616'
          }} data-id="element-146">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-900 bg-opacity-50 flex items-center justify-center mt-0.5" data-id="element-147">
                <XIcon className="w-4 h-4 text-red-500" data-id="element-148" />
              </div>
              <div data-id="element-149">
                <p className="text-neutral-100 font-semibold mb-1" data-id="element-150">
                  Creíste que la disciplina llegaría sola con el tiempo
                </p>
                <p className="text-neutral-500 text-sm leading-relaxed" data-id="element-151">
                  Pero solo llegó más agotamiento emocional. La disciplina sin
                  sistema es solo sufrimiento.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── THE MECHANISM ── */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{
      backgroundColor: '#0a0a0a'
    }} data-id="element-152">
        <div className="max-w-4xl mx-auto" data-id="element-153">
          <AnimatedSection data-id="element-154">
            <div className="inline-flex items-center gap-2 bg-red-700 bg-opacity-10 border border-red-800 rounded-full px-4 py-1.5 mb-6" data-id="element-155">
              <ZapIcon className="w-3.5 h-3.5 text-red-500" data-id="element-156" />
              <span className="text-red-400 text-xs font-semibold tracking-widest uppercase" data-id="element-157">
                El Mecanismo Único
              </span>
            </div>

            <h2 className="text-white mb-6" style={{
            fontFamily: 'Bebas Neue, sans-serif',
            fontSize: 'clamp(2.2rem, 5vw, 3.5rem)',
            letterSpacing: '0.03em'
          }} data-id="element-158">
              El Protocolo de Caja Negra
            </h2>
          </AnimatedSection>

          <AnimatedSection data-id="element-159">
            <div className="rounded-2xl p-8 md:p-10 border" style={{
            backgroundColor: '#130a0a',
            borderColor: '#7f1d1d',
            boxShadow: '0 0 40px rgba(220,38,38,0.08), inset 0 0 40px rgba(220,38,38,0.03)'
          }} data-id="element-160">
              <p className="text-neutral-200 text-lg md:text-xl leading-relaxed mb-6" data-id="element-161">
                Es un sistema de{' '}
                <span className="text-red-400 font-semibold" data-id="element-162">
                  enfriamiento cognitivo
                </span>{' '}
                diseñado para interrumpir el cortocircuito emocional en menos de{' '}
                <span className="text-white font-bold" data-id="element-163">5 minutos</span>.
              </p>
              <p className="text-neutral-300 text-base leading-relaxed mb-6" data-id="element-164">
                En lugar de luchar contra tus emociones, instalamos un{' '}
                <span className="text-amber-400 font-semibold" data-id="element-165">
                  "fusible" físico
                </span>{' '}
                que obliga a tu cerebro a desconectarse antes de que quemes la
                cuenta.
              </p>
              <div className="flex items-start gap-4 p-4 rounded-lg bg-black bg-opacity-40 border border-neutral-800" data-id="element-166">
                <AlertTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-167" />
                <p className="text-neutral-400 text-sm leading-relaxed" data-id="element-168">
                  Es la misma metodología que usan los{' '}
                  <span className="text-neutral-200 font-semibold" data-id="element-169">
                    pilotos de combate
                  </span>{' '}
                  para no entrar en pánico bajo fuego. Probada en entornos donde
                  el error cuesta la vida.
                </p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── WHAT YOU GET ── */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{
      backgroundColor: '#111111'
    }} data-id="element-170">
        <div className="max-w-4xl mx-auto" data-id="element-171">
          <AnimatedSection data-id="element-172">
            <SectionLabel data-id="element-173">Lo Que Obtienes</SectionLabel>
            <h2 className="text-white mb-10" style={{
            fontFamily: 'Bebas Neue, sans-serif',
            fontSize: 'clamp(2rem, 5vw, 3.2rem)',
            letterSpacing: '0.03em'
          }} data-id="element-174">
              Qué Incluye el Manual
            </h2>
          </AnimatedSection>

          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} className="space-y-4" data-id="element-175">
            <motion.div variants={fadeUp} className="flex items-start gap-4 p-5 rounded-xl border border-neutral-800" style={{
            backgroundColor: '#161616'
          }} data-id="element-176">
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-green-900 bg-opacity-40 border border-green-800 flex items-center justify-center mt-0.5" data-id="element-177">
                <FileTextIcon className="w-4 h-4 text-green-400" data-id="element-178" />
              </div>
              <div data-id="element-179">
                <p className="text-white font-semibold mb-1" data-id="element-180">
                  Manual PDF Completo "Cero Emoción"
                </p>
                <p className="text-neutral-400 text-sm leading-relaxed" data-id="element-181">
                  La psicología aplicada al trading de opciones. 45 minutos de
                  lectura que cambian tu relación con el mercado.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="flex items-start gap-4 p-5 rounded-xl border border-neutral-800" style={{
            backgroundColor: '#161616'
          }} data-id="element-182">
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-green-900 bg-opacity-40 border border-green-800 flex items-center justify-center mt-0.5" data-id="element-183">
                <CheckIcon className="w-4 h-4 text-green-400" data-id="element-184" />
              </div>
              <div data-id="element-185">
                <p className="text-white font-semibold mb-1" data-id="element-186">
                  Protocolo de Caja Negra
                </p>
                <p className="text-neutral-400 text-sm leading-relaxed" data-id="element-187">
                  Pasos exactos para detener el trading de revancha. Activado en
                  menos de 5 minutos después de una pérdida.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="flex items-start gap-4 p-5 rounded-xl border border-neutral-800" style={{
            backgroundColor: '#161616'
          }} data-id="element-188">
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-green-900 bg-opacity-40 border border-green-800 flex items-center justify-center mt-0.5" data-id="element-189">
                <CheckIcon className="w-4 h-4 text-green-400" data-id="element-190" />
              </div>
              <div data-id="element-191">
                <p className="text-white font-semibold mb-1" data-id="element-192">
                  La Regla del Fusible
                </p>
                <p className="text-neutral-400 text-sm leading-relaxed" data-id="element-193">
                  El sistema para blindar tu capital diario. Configúralo en tu
                  plataforma hoy mismo y nunca más quemes una cuenta.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="flex items-start gap-4 p-5 rounded-xl border border-neutral-800" style={{
            backgroundColor: '#161616'
          }} data-id="element-194">
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-green-900 bg-opacity-40 border border-green-800 flex items-center justify-center mt-0.5" data-id="element-195">
                <CheckIcon className="w-4 h-4 text-green-400" data-id="element-196" />
              </div>
              <div data-id="element-197">
                <p className="text-white font-semibold mb-1" data-id="element-198">
                  Checklist de Post-Loss Reset
                </p>
                <p className="text-neutral-400 text-sm leading-relaxed" data-id="element-199">
                  Cómo limpiar tu mente después de una pérdida y volver al día
                  siguiente en estado óptimo.
                </p>
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="flex items-start gap-4 p-5 rounded-xl border border-neutral-800" style={{
            backgroundColor: '#161616'
          }} data-id="element-200">
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-green-900 bg-opacity-40 border border-green-800 flex items-center justify-center mt-0.5" data-id="element-201">
                <CheckIcon className="w-4 h-4 text-green-400" data-id="element-202" />
              </div>
              <div data-id="element-203">
                <p className="text-white font-semibold mb-1" data-id="element-204">
                  Worksheet de Auditoría Forense
                </p>
                <p className="text-neutral-400 text-sm leading-relaxed" data-id="element-205">
                  Para convertir tus errores en datos útiles. Cada pérdida se
                  convierte en inteligencia de mercado personal.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── FOR / NOT FOR ── */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{
      backgroundColor: '#0a0a0a'
    }} data-id="element-206">
        <div className="max-w-5xl mx-auto" data-id="element-207">
          <AnimatedSection className="text-center mb-10" data-id="element-208">
            <h2 className="text-white" style={{
            fontFamily: 'Bebas Neue, sans-serif',
            fontSize: 'clamp(2rem, 5vw, 3.2rem)',
            letterSpacing: '0.03em'
          }} data-id="element-209">
              ¿Es Para Ti?
            </h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-210">
            {/* FOR */}
            <AnimatedSection data-id="element-211">
              <div className="rounded-2xl p-6 md:p-8 border border-green-900 h-full" style={{
              backgroundColor: '#0a1a0a'
            }} data-id="element-212">
                <h3 className="text-green-400 mb-6 text-xl" style={{
                fontFamily: 'Bebas Neue, sans-serif',
                letterSpacing: '0.05em'
              }} data-id="element-213">
                  ✓ Esto Es Para Ti Si...
                </h3>
                <ul className="space-y-4" data-id="element-214">
                  <li className="flex items-start gap-3" data-id="element-215">
                    <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-216" />
                    <span className="text-neutral-300 text-sm leading-relaxed" data-id="element-217">
                      Ganas dinero por la mañana y lo devuelves por la tarde
                      operando opciones.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-218">
                    <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-219" />
                    <span className="text-neutral-300 text-sm leading-relaxed" data-id="element-220">
                      Operas contratos de SPY, QQQ o acciones de gran
                      capitalización como NVIDIA o Tesla.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-221">
                    <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-222" />
                    <span className="text-neutral-300 text-sm leading-relaxed" data-id="element-223">
                      Sientes ansiedad, miedo o euforia excesiva al operar y
                      sabes que te afecta.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-224">
                    <CheckIcon className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-225" />
                    <span className="text-neutral-300 text-sm leading-relaxed" data-id="element-226">
                      Has quemado cuentas antes por no saber cuándo detenerte.
                    </span>
                  </li>
                </ul>
              </div>
            </AnimatedSection>

            {/* NOT FOR */}
            <AnimatedSection data-id="element-227">
              <div className="rounded-2xl p-6 md:p-8 border border-red-900 h-full" style={{
              backgroundColor: '#1a0a0a'
            }} data-id="element-228">
                <h3 className="text-red-400 mb-6 text-xl" style={{
                fontFamily: 'Bebas Neue, sans-serif',
                letterSpacing: '0.05em'
              }} data-id="element-229">
                  ✗ Esto No Es Para Ti Si...
                </h3>
                <ul className="space-y-4" data-id="element-230">
                  <li className="flex items-start gap-3" data-id="element-231">
                    <XIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-232" />
                    <span className="text-neutral-400 text-sm leading-relaxed" data-id="element-233">
                      Eres inversor a largo plazo que solo compra y mantiene
                      (Buy & Hold).
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-234">
                    <XIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-235" />
                    <span className="text-neutral-400 text-sm leading-relaxed" data-id="element-236">
                      Buscas una "estrategia mágica" de indicadores que gane
                      sola sin disciplina.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-237">
                    <XIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-238" />
                    <span className="text-neutral-400 text-sm leading-relaxed" data-id="element-239">
                      No estás dispuesto a seguir un protocolo de disciplina
                      estricto.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-240">
                    <XIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-241" />
                    <span className="text-neutral-400 text-sm leading-relaxed" data-id="element-242">
                      Prefieres culpar al mercado en lugar de examinar tu propio
                      comportamiento.
                    </span>
                  </li>
                </ul>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{
      backgroundColor: '#111111'
    }} data-id="element-243">
        <div className="max-w-4xl mx-auto" data-id="element-244">
          <AnimatedSection className="text-center mb-12" data-id="element-245">
            <SectionLabel data-id="element-246">El Proceso</SectionLabel>
            <h2 className="text-white" style={{
            fontFamily: 'Bebas Neue, sans-serif',
            fontSize: 'clamp(2rem, 5vw, 3.2rem)',
            letterSpacing: '0.03em'
          }} data-id="element-247">
              Cómo Funciona
            </h2>
          </AnimatedSection>

          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-248">
            <motion.div variants={fadeUp} className="relative p-6 rounded-2xl border border-neutral-800 text-center" style={{
            backgroundColor: '#161616'
          }} data-id="element-249">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5 border border-red-800" style={{
              backgroundColor: '#1a0808'
            }} data-id="element-250">
                <span className="text-red-500 font-bold text-2xl" style={{
                fontFamily: 'Bebas Neue, sans-serif'
              }} data-id="element-251">
                  01
                </span>
              </div>
              <h3 className="text-white font-bold mb-3" data-id="element-252">Auto-Diagnóstico</h3>
              <p className="text-neutral-400 text-sm leading-relaxed" data-id="element-253">
                Descargas el manual y realizas el auto-diagnóstico de tus
                disparadores emocionales específicos.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} className="relative p-6 rounded-2xl border border-red-900 text-center" style={{
            backgroundColor: '#1a0a0a'
          }} data-id="element-254">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5 border border-red-700" style={{
              backgroundColor: '#2d0a0a'
            }} data-id="element-255">
                <span className="text-red-400 font-bold text-2xl" style={{
                fontFamily: 'Bebas Neue, sans-serif'
              }} data-id="element-256">
                  02
                </span>
              </div>
              <h3 className="text-white font-bold mb-3" data-id="element-257">Instala el Fusible</h3>
              <p className="text-neutral-400 text-sm leading-relaxed" data-id="element-258">
                Instalas la "Regla del Fusible" en tu plataforma de trading hoy
                mismo. Funciona con cualquier broker.
              </p>
            </motion.div>

            <motion.div variants={fadeUp} className="relative p-6 rounded-2xl border border-neutral-800 text-center" style={{
            backgroundColor: '#161616'
          }} data-id="element-259">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-5 border border-red-800" style={{
              backgroundColor: '#1a0808'
            }} data-id="element-260">
                <span className="text-red-500 font-bold text-2xl" style={{
                fontFamily: 'Bebas Neue, sans-serif'
              }} data-id="element-261">
                  03
                </span>
              </div>
              <h3 className="text-white font-bold mb-3" data-id="element-262">
                Ejecuta el Protocolo
              </h3>
              <p className="text-neutral-400 text-sm leading-relaxed" data-id="element-263">
                Ejecutas el Protocolo de Caja Negra cada vez que una operación
                toque tu Stop Loss. Sin excepciones.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── GUARANTEE ── */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{
      backgroundColor: '#0a0a0a'
    }} data-id="element-264">
        <div className="max-w-3xl mx-auto" data-id="element-265">
          <AnimatedSection data-id="element-266">
            <div className="rounded-2xl p-8 md:p-12 border text-center" style={{
            backgroundColor: '#0f0f0f',
            borderColor: '#292929'
          }} data-id="element-267">
              <ShieldCheckIcon className="w-12 h-12 text-amber-500 mx-auto mb-4" data-id="element-268" />
              <div className="text-amber-400 mb-2" style={{
              fontFamily: 'Bebas Neue, sans-serif',
              fontSize: 'clamp(4rem, 12vw, 7rem)',
              letterSpacing: '0.05em',
              lineHeight: 1
            }} data-id="element-269">
                14 DÍAS
              </div>
              <p className="text-neutral-300 text-lg font-semibold mb-4" data-id="element-270">
                Cambio Radical Garantizado
              </p>
              <p className="text-neutral-400 text-base leading-relaxed max-w-xl mx-auto" data-id="element-271">
                En los próximos 14 días verás un cambio radical: dejarás de
                sentir ese vacío en el estómago al cerrar la sesión.{' '}
                <span className="text-neutral-200" data-id="element-272">
                  No prometemos que ganarás cada operación
                </span>
                , pero sí garantizamos que ninguna pérdida volverá a ser
                catastrófica para tu cuenta ni para tu paz mental.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{
      backgroundColor: '#111111'
    }} data-id="element-273">
        <div className="max-w-3xl mx-auto" data-id="element-274">
          <AnimatedSection className="text-center mb-10" data-id="element-275">
            <SectionLabel data-id="element-276">Preguntas Frecuentes</SectionLabel>
            <h2 className="text-white" style={{
            fontFamily: 'Bebas Neue, sans-serif',
            fontSize: 'clamp(2rem, 5vw, 3.2rem)',
            letterSpacing: '0.03em'
          }} data-id="element-277">
              FAQ
            </h2>
          </AnimatedSection>

          <AnimatedSection data-id="element-278">
            <div className="space-y-3" data-id="element-279">
              <FAQItem question="¿Necesito una plataforma especial?" answer="No, el protocolo funciona con cualquier broker: Thinkorswim, Interactive Brokers, Robinhood, Tastytrade, y cualquier otro. Es un sistema mental, no técnico." data-id="element-280" />
              <FAQItem question="¿Cuánto tiempo toma leerlo?" answer="Unos 45 minutos. Está diseñado para ser leído hoy mismo antes de dormir, para que mañana en la apertura ya tengas el protocolo instalado." data-id="element-281" />
              <FAQItem question="¿Sirve para cuentas pequeñas?" answer="Es vital para cuentas pequeñas. Con una cuenta pequeña, un solo error emocional puede ser el fin. El Protocolo de Caja Negra es tu escudo más importante cuando el margen de error es mínimo." data-id="element-282" />
              <FAQItem question="¿Es difícil de aplicar?" answer="Es simple, pero requiere honestidad brutal contigo mismo. No hay trucos complicados — solo un sistema claro que funciona si lo sigues. La dificultad no está en entenderlo, sino en ejecutarlo cuando el mercado te presiona." data-id="element-283" />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── PRICE BLOCK + FINAL CTA ── */}
      <section id="precio" className="py-20 px-4 sm:px-6 lg:px-8" style={{
      backgroundColor: '#0a0a0a'
    }} data-id="element-284">
        <div className="max-w-2xl mx-auto text-center" data-id="element-285">
          <AnimatedSection data-id="element-286">
            <div className="rounded-3xl p-10 md:p-14 border" style={{
            backgroundColor: '#111111',
            borderColor: '#292929',
            boxShadow: '0 0 80px rgba(220,38,38,0.06)'
          }} data-id="element-287">
              <p className="text-red-500 text-xs font-bold tracking-widest uppercase mb-4" data-id="element-288">
                Oferta de Lanzamiento
              </p>
              <h2 className="text-white mb-3" style={{
              fontFamily: 'Bebas Neue, sans-serif',
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              letterSpacing: '0.03em'
            }} data-id="element-289">
                Protege Tu Capital Hoy
              </h2>
              <p className="text-neutral-400 text-base mb-8 leading-relaxed" data-id="element-290">
                Menos de lo que pierdes en un mal spread de opciones. Un pago
                único, acceso de por vida.
              </p>

              <div className="mb-8" data-id="element-291">
                <div className="flex items-center justify-center gap-4 mb-2" data-id="element-292">
                  <span className="text-neutral-500 line-through text-2xl md:text-3xl" style={{
                  fontFamily: 'Bebas Neue, sans-serif',
                  letterSpacing: '0.05em'
                }} data-id="element-293">
                    $27.00 USD
                  </span>
                  <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded uppercase tracking-widest" data-id="element-294">
                    77% OFF
                  </span>
                </div>
                <div className="text-amber-400 font-bold" style={{
                fontFamily: 'Bebas Neue, sans-serif',
                fontSize: 'clamp(3.5rem, 10vw, 5.5rem)',
                letterSpacing: '0.05em',
                lineHeight: 1
              }} data-id="element-295">
                  $6.37 USD
                </div>
                <p className="text-neutral-500 text-sm mt-2" data-id="element-296">
                  Pago único · Sin suscripción · Sin sorpresas
                </p>
              </div>

              <a href="https://bienestarenminutos.com/cart/42999615258714:1" target="_blank" rel="noopener noreferrer" className="cta-pulse inline-flex items-center justify-center gap-3 bg-red-600 hover:bg-red-500 text-white font-bold text-lg px-10 py-5 rounded-xl transition-colors duration-200 w-full mb-5" style={{
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-297">
                <DownloadIcon className="w-5 h-5" data-id="element-298" />
                DESCARGAR EL MANUAL AHORA
              </a>

              <p className="text-neutral-500 text-sm mb-8" data-id="element-299">
                Acceso inmediato después del pago. Prepárate para la apertura de
                mañana.
              </p>

              {/* Trust indicators */}
              <div className="flex flex-wrap items-center justify-center gap-6 pt-6 border-t border-neutral-800" data-id="element-300">
                <div className="flex items-center gap-2 text-neutral-500 text-xs" data-id="element-301">
                  <LockIcon className="w-4 h-4 text-green-600" data-id="element-302" />
                  <span data-id="element-303">Pago Seguro</span>
                </div>
                <div className="flex items-center gap-2 text-neutral-500 text-xs" data-id="element-304">
                  <ZapIcon className="w-4 h-4 text-amber-600" data-id="element-305" />
                  <span data-id="element-306">Acceso Inmediato</span>
                </div>
                <div className="flex items-center gap-2 text-neutral-500 text-xs" data-id="element-307">
                  <FileTextIcon className="w-4 h-4 text-blue-600" data-id="element-308" />
                  <span data-id="element-309">Formato PDF</span>
                </div>
                <div className="flex items-center gap-2 text-neutral-500 text-xs" data-id="element-310">
                  <ShieldCheckIcon className="w-4 h-4 text-green-600" data-id="element-311" />
                  <span data-id="element-312">Garantía 14 Días</span>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-8 px-4 border-t border-neutral-900" style={{
      backgroundColor: '#0a0a0a'
    }} data-id="element-313">
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-4" data-id="element-314">
          <div className="flex items-center gap-6" data-id="element-315">
            <button onClick={() => setLegalModal('privacy')} className="text-neutral-500 hover:text-neutral-300 text-xs transition-colors duration-200 underline underline-offset-2" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-316">
              Política de Privacidad
            </button>
            <span className="text-neutral-700 text-xs" data-id="element-317">·</span>
            <button onClick={() => setLegalModal('terms')} className="text-neutral-500 hover:text-neutral-300 text-xs transition-colors duration-200 underline underline-offset-2" style={{
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-318">
              Términos de Servicio
            </button>
          </div>
          <p className="text-neutral-700 text-xs leading-relaxed text-center" style={{
          fontFamily: 'Inter, sans-serif'
        }} data-id="element-319">
            © 2024 Cero Emoción. Este producto es un manual educativo de
            psicología del trading. Los resultados individuales varían. El
            trading de opciones conlleva riesgo de pérdida de capital. No somos
            asesores financieros registrados.
          </p>
        </div>
      </footer>
    </main>;
}